import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { Box, Button, Card, CardContent } from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import MUIDataTable, { SelectableRows } from 'mui-datatables';
import { RouteComponentProps } from 'react-router';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { IWorkflowService } from '../../../services/Interfaces/IWorkflowService';
import { IOrganizationService } from '../../../services/Interfaces/IOrganizationService';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import { isNullOrUndefined } from 'util';
import {
  WorkflowPartner,
  CopyFormConfigurationRequestDto,
  CopyFormConfigurationItemResultDto
} from '../../../interfaces/Workflow';

interface IWorkflowCaseStructureCopyConfigurationState {
  workflowId: number;
  isLoading: boolean;
  workflowPartners: WorkflowPartner[];
  sourceWorkflowPartner: WorkflowPartner | null;
  targetWorkflowPartners: WorkflowPartner[];
  results: CopyFormConfigurationItemResultDto[];
}

type IWorkflowCaseStructureCopyConfigurationProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string; workflowId: string }>;

class WorkflowCaseStructureCopyConfigurationTab extends React.PureComponent<
  IWorkflowCaseStructureCopyConfigurationProps,
  IWorkflowCaseStructureCopyConfigurationState
> {
  private translatorService!: ITranslatorService;
  private workflowService!: IWorkflowService;
  private organizationService!: IOrganizationService;

  static contextType = ApplicationContext;

  state = {
    workflowId: 0,
    isLoading: false,
    workflowPartners: [],
    sourceWorkflowPartner: null,
    targetWorkflowPartners: [],
    results: []
  } as IWorkflowCaseStructureCopyConfigurationState;

  public componentDidMount() {
    const workflowId = Number.parseInt(this.props.match.params.workflowId);
    if (Number.isNaN(workflowId)) {
      return;
    }

    this.setState(
      {
        isLoading: true,
        workflowId
      },
      async () => {
        await this.loadWorkflowPartners();
      }
    );
  }

  loadWorkflowPartners = async () => {
    const workflowPartnersList = await this.workflowService.GetWorkflowPartners(
      this.state.workflowId
    );

    const partnerIds = (workflowPartnersList || []).map((wp) => wp.partnerId);
    const organizations =
      partnerIds.length > 0
        ? await this.organizationService.GetOrganizationsByIds(partnerIds)
        : [];

    (workflowPartnersList || []).forEach((wp: any) => {
      const partner = organizations.find((item: any) => item.id === wp.partnerId);
      wp.partner = isNullOrUndefined(partner) ? null : partner;
      wp.displayName = partner?.displayName || '';
    });

    this.setState({
      workflowPartners: workflowPartnersList || [],
      isLoading: false
    });
  };

  handleSourceChange = (newValue: WorkflowPartner | null) => {
    const filteredTargets = this.state.targetWorkflowPartners.filter(
      (t) => t.id !== newValue?.id
    );
    this.setState({
      sourceWorkflowPartner: newValue,
      targetWorkflowPartners: filteredTargets
    });
  };

  handleTargetsChange = (newValue: WorkflowPartner[]) => {
    this.setState({ targetWorkflowPartners: newValue });
  };

  copyFormConfiguration = async () => {
    const { sourceWorkflowPartner, targetWorkflowPartners, workflowId } = this.state;
    if (sourceWorkflowPartner === null || targetWorkflowPartners.length === 0) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate(
          'WORKFLOWS_COPY_CONFIG_MISSING_SELECTION',
          'Selecteaza sursa si cel putin un target.'
        ),
        { variant: 'warning' }
      );
      return;
    }

    const request: CopyFormConfigurationRequestDto = {
      workflowId,
      sourceWorkflowPartnerId: sourceWorkflowPartner.id,
      targetWorkflowPartnerIds: targetWorkflowPartners.map((t) => t.id)
    };

    this.setState({ isLoading: true, results: [] });
    try {
      const response = await this.workflowService.CopyFormConfiguration(request);
      this.setState({ results: response.results || [], isLoading: false });
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('WORKFLOWS_COPY_CONFIG_DONE', 'Copiere finalizata.'),
        { variant: 'success' }
      );
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('WORKFLOWS_COPY_CONFIG_ERROR', 'Eroare la copiere.'),
        { variant: 'error' }
      );
    }
  };

  getPartnerOptions = (excludeId: number | null = null): WorkflowPartner[] => {
    return this.state.workflowPartners
      .filter((p) => excludeId === null || p.id !== excludeId)
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  };

  renderAddForm() {
    return (
      <Box mt={1} pt={1}>
        <Card elevation={1}>
          <CardContent>
            <ValidatorForm onSubmit={this.copyFormConfiguration}>
              <Autocomplete
                id="sourceWorkflowPartner"
                className="m-2"
                fullWidth
                options={this.state.workflowPartners.sort(function (a: any, b: any) {
                  return (a.displayName || '').localeCompare(b.displayName || '');
                })}
                value={this.state.sourceWorkflowPartner}
                onChange={(e: any, newValue: WorkflowPartner | null) =>
                  this.handleSourceChange(newValue)
                }
                getOptionLabel={(option: any) => option.displayName || ''}
                renderInput={(params) => (
                  <TextValidator
                    {...params}
                    name="sourceWorkflowPartner"
                    value={this.state.sourceWorkflowPartner}
                    label={this.translatorService.Tranlate(
                      'WORKFLOWS_COPY_CONFIG_SOURCE',
                      'Partener Model'
                    )}
                    fullWidth
                    validators={['required']}
                    errorMessages={[
                      this.translatorService.Tranlate(
                        'VALIDATORS_REQUIRED',
                        'Campul este obligatoriu'
                      )
                    ]}
                  />
                )}
              />

              <Autocomplete
                id="targetWorkflowPartners"
                className="m-2"
                fullWidth
                multiple
                options={this.getPartnerOptions(this.state.sourceWorkflowPartner?.id ?? null)}
                value={this.state.targetWorkflowPartners}
                onChange={(e: any, newValue: any[]) => this.handleTargetsChange(newValue)}
                getOptionLabel={(option: any) => option.displayName || ''}
                renderInput={(params) => (
                  <TextValidator
                    {...params}
                    name="targetWorkflowPartners"
                    value={this.state.targetWorkflowPartners}
                    label={this.translatorService.Tranlate(
                      'WORKFLOWS_COPY_CONFIG_TARGETS',
                      'Parteneri (target)'
                    )}
                    fullWidth
                    validators={['required']}
                    errorMessages={[
                      this.translatorService.Tranlate(
                        'VALIDATORS_REQUIRED',
                        'Campul este obligatoriu'
                      )
                    ]}
                  />
                )}
              />

              <div className="text-right">
                <Button className="m-2" variant="contained" color="secondary" type="submit">
                  {this.translatorService.Tranlate('WORKFLOWS_COPY_CONFIG_APPLY', 'Aplica')}
                </Button>
              </div>
            </ValidatorForm>
          </CardContent>
        </Card>
      </Box>
    );
  }

  getPartnerNameById = (workflowPartnerId: number): string => {
    const match = this.state.workflowPartners.find((p: any) => p.id === workflowPartnerId) as any;
    return match?.displayName || '';
  };

  getResultColumns = () => [
    {
      name: 'targetWorkflowPartnerId',
      label: this.translatorService.Tranlate('WORKFLOW_TARIFFS_TAB_WORKFLOW_PARTNER_HEADER', 'Partener'),
      options: {
        customBodyRender: (value: number) => this.getPartnerNameById(value)
      }
    },
    {
      name: 'copiedPermissionsCount',
      label: this.translatorService.Tranlate('WORKFLOWS_TAB_PERMISSIONS', 'Permisiuni')
    },
    {
      name: 'copiedCalculationsCount',
      label: this.translatorService.Tranlate('WORKFLOWS_TAB_CALCULATIONS', 'Calculatii')
    },
    {
      name: 'copiedReportsCount',
      label: this.translatorService.Tranlate('ORGANIZATION_FORM_EDIT_REPORTS_TAB', 'Rapoarte')
    },
    {
      name: 'copiedOptimizationsCount',
      label: this.translatorService.Tranlate('WORKFLOWS_TAB_OPTIMIZATION', 'Optimizari')
    },
    {
      name: 'message',
      label: this.translatorService.Tranlate('WORKFLOWS_COPY_CONFIG_COL_MSG', 'Mesaj')
    }
  ];

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.workflowService = (this.context as AppContext).workflowService;
    this.organizationService = (this.context as AppContext).organizationService;

    const options = {
      viewColumns: false,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      elevation: 1
    };

    return (
      <Fragment>
        {this.state.isLoading ? (
          <Box className="text-center m-3">
            <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
          </Box>
        ) : (
          <Fragment>
            {this.renderAddForm()}
            {this.state.results.length > 0 ? (
              <Box mt={1} pt={1}>
                <MUIDataTable
                  title={this.translatorService.Tranlate(
                    'WORKFLOWS_COPY_CONFIG_RESULTS_TITLE',
                    'Rezultat copiere'
                  )}
                  data={this.state.results}
                  columns={this.getResultColumns()}
                  options={options}
                />
              </Box>
            ) : null}
          </Fragment>
        )}
      </Fragment>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    appState: state.app
  }),
  null
)(withSnackbar(WorkflowCaseStructureCopyConfigurationTab as any));
